import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Tabs,
  Typography,
  Progress,
  Table,
  Tag,
  Button,
  Space,
  Input,
  Dropdown,
  Menu,
  Statistic,
  message,
  Alert,
  Select,
  Checkbox,
  Divider,
  Tooltip,
  Modal
} from 'antd';
import {
  TruckOutlined,
  FileTextOutlined,
  InboxOutlined,
  EditOutlined,
  PrinterOutlined,
  MenuOutlined,
  SearchOutlined,
  MoreOutlined,
  PlusOutlined,
  FilterOutlined,
  CloseOutlined,
  DownOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import Navbar from '../../components/Navbar';
import InclusaoNotasModal from '../../components/InclusaoNotasModal';
import VehiclePlateModal from '../../components/VehiclePlateModal';
import CDComponent from '../../components/cd';
import PrinterLoader from '../../components/PrinterLoader';
import Footer from './Footer';
import { OnboardProvider, useOnboard } from './OnboardContext';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import notaanota from '../../services/notaanota';
import printerService, { enviarEtiquetaInicial, verificaImpressora, imprimir } from '../../services/printerService';
import { useOrganization } from '../../contexts/OrganizationContext';
import PrinterSelection from '../../components/PrinterSelection';
import '../../index.css';
import './Onboard.css';



const formatarDataHora = (val) => {
  if (!val) return '---';
  if (typeof val === 'string') {
    const m = val.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);
    if (m) {
      return `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}`;
    }
  }
  return dayjs(val).format('DD/MM/YYYY HH:mm');
};

const { Title } = Typography;

// Componente principal que usa o contexto Onboard
const Onboard = () => {
  return (
    <OnboardProvider>
      <OnboardContent />
    </OnboardProvider>
  );
};

// Componente interno que usa o contexto Onboard
const OnboardContent = () => {
  const [activeTab, setActiveTab] = useState('coletas');
  const [modalVisible, setModalVisible] = useState(false);
  const [pendingBarcode, setPendingBarcode] = useState('');
  const [notaEncontradaModal, setNotaEncontradaModal] = useState(null);
  const {
    romaneioInfo,
    userInfo,
    vehicleData,
    empresasData,
    totals,
    loading,
    error,
    coletas,
    coletasLoading,
    coletasMensagem,
    buscarNotasTotais,
    updateOnboardData,
    updateTotals,
    clienteSelecionado,
    selecionarCliente,
    veiculoDoDia,
    modalPlacaVisible,
    setModalPlacaVisible,
    setVeiculoDoDia,
    onboardData,
    setOnboardData,
    notaAtual,
    setNotaAtual,
    printerName,
    printerConnected,
    buscaPath
  } = useOnboard();
  const { isAuthenticated, user } = useAuth();
  const { } = useOrganization();

  // Estado para expandir/recolher notas individualmente
  const [expandedNotas, setExpandedNotas] = useState(() => new Set());
  const [reimpressaoLoading, setReimpressaoLoading] = useState(false);
  const [reimpressaoError, setReimpressaoError] = useState(null);
  const [reimpressaoRows, setReimpressaoRows] = useState([]);
  const [reimpOrderBy, setReimpOrderBy] = useState('cliente');
  const [reimpOrder, setReimpOrder] = useState('desc');
  const [printerModalVisible, setPrinterModalVisible] = useState(false);
  const [alteracoesLoading, setAlteracoesLoading] = useState(false);
  const [alteracoesError, setAlteracoesError] = useState(null);
  const [alteracoesRows, setAlteracoesRows] = useState([]);
  const [reprintError, setReprintError] = useState('');
  const [vehicleSelectionVisible, setVehicleSelectionVisible] = useState(false);
  const [userUsages, setUserUsages] = useState([]);
  const [vehicleSelectionLoading, setVehicleSelectionLoading] = useState(false);
  const [vehicleSelectionShown, setVehicleSelectionShown] = useState(false);
  const [cdModalVisible, setCdModalVisible] = useState(false);
  const [roteirizacaoModalVisible, setRoteirizacaoModalVisible] = useState(false);
  const [encerrarModalVisible, setEncerrarModalVisible] = useState(false);

  // Estados para produtividade
  const [produtividadeLoading, setProdutividadeLoading] = useState(false);

  useEffect(() => {
    try { printerService.setUser(user || null); } catch { }
  }, [user])
  /*
  const toggleNotaExpand = (idKey) => {
    setExpandedNotas((prev) => {
      const next = new Set(prev);
      if (next.has(idKey)) {
        next.delete(idKey);
      } else {
        next.add(idKey);
      }
      return next;
    });
  };
*/

  // Alternar expansão/recolhimento com classe CSS (igual ao modelo)
  const toggleNotaExpansao = (notaId) => {
    const el = document.getElementById(`nota-${notaId}`);
    if (el) {
      el.classList.toggle('nota-expanded');
    }
  };

  const fetchClientNotes = useCallback(async (cnpj, fallbackNotas = []) => {
    if (!veiculoDoDia) return;

    const cleanCnpj = String(cnpj || '').replace(/\D/g, '');

    // Se temos fallbackNotas passadas ou notas já presentes no clienteSelecionado, usa imediatamente
    const initialNotas = (Array.isArray(fallbackNotas) && fallbackNotas.length > 0)
      ? fallbackNotas
      : ((clienteSelecionado?.notas && Array.isArray(clienteSelecionado.notas) && clienteSelecionado.notas.length > 0)
          ? clienteSelecionado.notas
          : []);

    if (initialNotas.length > 0) {
      setOnboardData(prev => ({
        ...prev,
        notasDoCliente: initialNotas,
      }));
    }

    try {
      const dtUso = (onboardData?.dtUso ? String(onboardData.dtUso).substring(0, 10) : '') || dayjs().format('YYYY-MM-DD');
      const veiculoId = veiculoDoDia?.veiculo?.id || veiculoDoDia?.veiculoId || veiculoDoDia?.id;

      const notas = await apiService.notaVeiculoUso.search({
        dia: dtUso,
        veiculoId,
        cnpj: cleanCnpj || cnpj,
      });

      const notasArray = Array.isArray(notas) ? notas : (notas?.data || []);
      const notasFiltradas = notasArray.map((n) => {
        try {
          const baseNota = n?.nota || {};
          const dM3 = baseNota?.detalhe?.m3;

          // Normalizar cidade e UF se estiverem faltando no destinatário
          if (baseNota.destinatario) {
            if (!baseNota.destinatario.cidade && baseNota.cidade) {
              baseNota.destinatario.cidade = baseNota.cidade;
            }
            if (!baseNota.destinatario.uf && baseNota.uf) {
              baseNota.destinatario.uf = baseNota.uf;
            }
          }

          if (dM3 != null) {
            return { ...n, nota: { ...baseNota, m3: dM3 } };
          }
          return { ...n, nota: baseNota };
        } catch {
          return n;
        }
      });

      // Se alguma nota estiver sem cidade ou a cidade for igual à rota, busca o nome da cidade pelo CEP
      const cepsPendentes = new Set();
      notasFiltradas.forEach(item => {
        const bn = item?.nota || {};
        const dest = bn?.destinatario || {};
        const cidadeAtual = String(dest?.cidade || bn?.cidade || '').trim();
        const rotaAtual = String(bn?.rota || '').trim();
        const cepRaw = bn?.cepentrega || bn?.detalhe?.cepentrega || dest?.cep || bn?.cep || '';
        const cleanCep = String(cepRaw).replace(/\D/g, '');
        if ((!cidadeAtual || (rotaAtual && cidadeAtual.toUpperCase() === rotaAtual.toUpperCase())) && cleanCep.length === 8) {
          cepsPendentes.add(cleanCep);
        }
      });

      if (cepsPendentes.size > 0) {
        try {
          const cepMap = new Map();
          await Promise.all(
            Array.from(cepsPendentes).map(async (cep) => {
              try {
                const res = await apiService.get(`/v1/siscep/cep/${cep}`);
                if (res?.dslocal) {
                  cepMap.set(cep, { cidade: res.dslocal, uf: res.dsuf });
                }
              } catch { }
            })
          );

          if (cepMap.size > 0) {
            notasFiltradas.forEach(item => {
              const bn = item?.nota || {};
              const dest = bn?.destinatario || {};
              const cepRaw = bn?.cepentrega || bn?.detalhe?.cepentrega || dest?.cep || bn?.cep || '';
              const cleanCep = String(cepRaw).replace(/\D/g, '');
              const dadosCep = cepMap.get(cleanCep);
              if (dadosCep) {
                if (!item.nota) item.nota = {};
                if (!item.nota.destinatario) item.nota.destinatario = {};
                item.nota.destinatario.cidade = dadosCep.cidade;
                item.nota.destinatario.uf = dadosCep.uf;
                item.nota.cidade = dadosCep.cidade;
                item.nota.uf = dadosCep.uf;
              }
            });
          }
        } catch (e) {
          console.warn('Erro ao resolver cidades por CEP:', e);
        }
      }

      if (notasFiltradas.length > 0 || initialNotas.length === 0) {
        setOnboardData(prev => ({
          ...prev,
          notasDoCliente: notasFiltradas,
        }));
      }
    } catch (err) {
      console.error("Erro ao buscar notas do cliente:", err);
    }
  }, [onboardData?.dtUso, veiculoDoDia, clienteSelecionado?.notas, setOnboardData]);

  const fetchReimpressao = useCallback(async () => {
    try {
      setReimpressaoError(null);
      if (!veiculoDoDia || !onboardData?.dtUso) return;
      setReimpressaoLoading(true);
      const veiculoId = veiculoDoDia?.veiculo?.id || veiculoDoDia?.veiculoId || veiculoDoDia?.id;
      const data = onboardData.dtUso;
      const res = await notaanota.getReimpressaoPorDia(veiculoId, data);
      const arr = Array.isArray(res) ? res : [];
      setReimpressaoRows(arr);
    } catch (e) {
      setReimpressaoError(e);
      setReimpressaoRows([]);
    } finally {
      setReimpressaoLoading(false);
    }
  }, [veiculoDoDia, onboardData.dtUso]);

  // Estados de filtros/ordenação de notas
  const [ordenacaoNotas, setOrdenacaoNotas] = useState('Nota');
  const [filtroNota, setFiltroNota] = useState('');
  const [filtroAtivo, setFiltroAtivo] = useState(false);
  const [selecionarNaoImpressas, setSelecionarNaoImpressas] = useState(false);
  const [notasCarregadas, setNotasCarregadas] = useState(false);
  const [usageToClose, setUsageToClose] = useState(null);

  // Helper: formata data local YYYY-MM-DD
  const toLocalYYYYMMDD = (date) => {
    try {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    } catch {
      return '';
    }
  };

  const toSpYYYYMMDD = (date) => {
    try {
      const d = new Date(date);
      const parts = new Intl.DateTimeFormat('sv-SE', {
        timeZone: 'America/Sao_Paulo',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).formatToParts(d);
      const get = (t) => parts.find((p) => p.type === t)?.value || '00';
      return `${get('year')}-${get('month')}-${get('day')}`;
    } catch {
      return '';
    }
  };



  // Menu dropdown para a aba de menu - usando useMemo para evitar re-Criação
  const menuItems = useMemo(() => [
    {
      key: '2',
      label: 'Centros de Distribuição',
      icon: <FileTextOutlined />
    },
    {
      key: '3',
      label: 'Roteirização',
      icon: <TruckOutlined />
    },
    {
      key: '1',
      label: 'Encerrar coletas',
      icon: <TruckOutlined style={{ color: '#ff4d4f', transform: 'scaleX(-1)' }} />
    }
  ], []);

  // Ouve evento global de leitura de código de barras e abre a modal
  // Importante: este hook deve ficar ANTES de qualquer retorno condicional
  // para manter a ordem/quantidade de hooks consistente entre renders
  useEffect(() => {
    const handleBarcodeRead = (evt) => {
      try {
        const code = evt?.detail?.code || '';
        if (code) {
          const key = String(code).trim();
          // Busca nota pela chave na estrutura notasDoCliente
          try {
            const lista = onboardData?.notasDoCliente || [];
            const encontrada = lista.find(n => String(n?.nota?.chave || '') === key);
            if (encontrada) {
              encontrada.placa = veiculoDoDia?.placa || veiculoDoDia?.veiculo?.placa || onboardData?.vehicleData?.placa || '';
              setNotaAtual(encontrada);
              setNotaEncontradaModal(encontrada);
            }
          } catch { }
          setPendingBarcode(String(code));
          setModalVisible(true);
        }
      } catch (err) { }
    };

    window.addEventListener('onBarcodeRead', handleBarcodeRead);
    return () => {
      window.removeEventListener('onBarcodeRead', handleBarcodeRead);
    };
  }, [onboardData?.notasDoCliente]);

  // Função para calcular estatísticas de produtividade baseada nos dados existentes
  const calcularEstatisticasProdutividade = useCallback(() => {
    if (!onboardData?.totaisPorCliente || !Array.isArray(onboardData.totaisPorCliente)) {
      return;
    }

    const totais = onboardData.totaisPorCliente;
    let notasImpressas = 0;
    let notasNaoImpressas = 0;
    let etiquetasImpressas = 0;
    let etiquetasNaoImpressas = 0;

    totais.forEach(cliente => {
      notasImpressas += Number(cliente.notasImpressas || 0);
      notasNaoImpressas += Number(cliente.notasNaoImpressas || 0);
      etiquetasImpressas += Number(cliente.volumesImpressosTotal || 0);
      etiquetasNaoImpressas += Number(cliente.volumesNaoImpressosTotal || 0);
    });

    // Atualizar os totais globais no contexto para que o layout possa usá-los
    updateTotals({
      notasImpressas,
      notasNaoImpressas,
      etiquetasImpressas,
      etiquetasNaoImpressas,
      clientes: totais.length
    });

    // Preparar dados para a tabela de clientes
    const clientesData = totais.map(cliente => ({
      key: cliente.cnpj || cliente.CdInscricao,
      cliente: cliente.DsEntidade || cliente.dsremetente || 'Cliente não identificado',
      primeiraEtiqueta: '---', // TODO: implementar cálculo por cliente
      ultimaEtiqueta: '---'   // TODO: implementar cálculo por cliente
    }));

    setProdutividadeClientes(clientesData);

    // Buscar primeira e última etiqueta usando nota_veiculo_usos
    const buscarEtiquetasExtremas = async () => {
      try {
        const hoje = new Date();
        const ano = hoje.getFullYear();
        const mes = String(hoje.getMonth() + 1).padStart(2, '0');
        const dia = String(hoje.getDate()).padStart(2, '0');
        const diaFormatado = `${ano}-${mes}-${dia}`;

        const placaAtual = veiculoDoDia?.placa || veiculoDoDia?.veiculo?.placa || onboardData?.vehicleData?.placa;
        if (placaAtual && user?.id) {
          const notaVeiculoUsos = await apiService.notaVeiculoUso.list({
            placa: placaAtual,
            dia: diaFormatado,
            usuarioId: user.id
          });

          if (notaVeiculoUsos?.data && notaVeiculoUsos.data.length > 0) {
            // Encontrar a primeira etiqueta (menor createdAt)
            const primeira = notaVeiculoUsos.data.reduce((min, item) => {
              const currentDate = item.createdAt;
              return (!min || (currentDate && String(currentDate) < String(min))) ? currentDate : min;
            }, null);

            // Encontrar a última etiqueta (maior createdAt ou ultima_etiqueta)
            const ultima = notaVeiculoUsos.data.reduce((max, item) => {
              const currentDate = item.ultima_etiqueta || item.createdAt;
              return (!max || (currentDate && String(currentDate) > String(max))) ? currentDate : max;
            }, null);

            // Atualizar os totais globais no contexto
            updateTotals({
              primeiraEtiqueta: primeira,
              ultimaEtiqueta: ultima
            });
          }
        }
      } catch (error) {
        console.error('Erro ao buscar etiquetas extremas:', error);
      }
    };

    buscarEtiquetasExtremas();
  }, [onboardData?.totaisPorCliente, veiculoDoDia, onboardData?.vehicleData, user?.id]);

  useEffect(() => {
    if (activeTab === 'reimpressoes') {
      fetchReimpressao();
    } else if (activeTab === 'produtividade') {
      calcularEstatisticasProdutividade();
    }
  }, [activeTab, fetchReimpressao, calcularEstatisticasProdutividade]);

  const fetchAlteracoes = useCallback(async () => {
    setAlteracoesLoading(true);
    setAlteracoesError(null);
    try {
      const dia = dayjs().format('YYYY-MM-DD');
      const rows = await apiService.get(`/v1/notas/historico?dia=${dia}`);
      setAlteracoesRows(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setAlteracoesError(err?.message || 'Falha ao carregar alterações');
      setAlteracoesRows([]);
    } finally {
      setAlteracoesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'alteracoes') {
      fetchAlteracoes();
    }
  }, [activeTab, fetchAlteracoes]);

  const alteracoesGrouped = useMemo(() => {
    try {
      const byRemetente = new Map();
      (alteracoesRows || []).forEach((row) => {
        const clienteKey = String(row?.remetenteNome || 'Sem Remetente');
        let grupo = byRemetente.get(clienteKey);
        if (!grupo) {
          grupo = { id: `grp-${clienteKey}`, remetenteNome: clienteKey, isGroup: true, children: [] };
          byRemetente.set(clienteKey, grupo);
        }
        const noteKey = `${row?.nota || ''}-${row?.serie || ''}`;
        let noteMap = grupo.noteMap;
        if (!noteMap) {
          noteMap = new Map();
          grupo.noteMap = noteMap;
        }
        let noteGroup = noteMap.get(noteKey);
        if (!noteGroup) {
          noteGroup = { id: `note-${noteKey}`, isNoteGroup: true, nota: row?.nota, serie: row?.serie, chave: row?.chave, children: [] };
          noteMap.set(noteKey, noteGroup);
          grupo.children.push(noteGroup);
        }
        noteGroup.children.push({ ...row, isGroup: false, isNoteGroup: false });
      });
      const groups = Array.from(byRemetente.values());
      groups.forEach(g => {
        if (Array.isArray(g.children)) {
          g.children.sort((a, b) => String(a.nota || '').localeCompare(String(b.nota || '')));
          g.children.forEach(ng => {
            if (Array.isArray(ng.children)) {
              ng.children.sort((a, b) => String(b.dataOperacao || '').localeCompare(String(a.dataOperacao || '')));
            }
          });
        }
        delete g.noteMap;
      });
      groups.sort((a, b) => String(a.remetenteNome || '').localeCompare(String(b.remetenteNome || '')));
      return groups;
    } catch {
      return [];
    }
  }, [alteracoesRows]);

  const reimpressaoGrouped = useMemo(() => {
    try {
      const byCliente = new Map();
      (reimpressaoRows || []).forEach((row) => {
        const k = String(row?.remetenteNome || row?.cnpj || 'Sem Cliente');
        const arr = byCliente.get(k) || [];
        arr.push({ ...row, isGroup: false });
        byCliente.set(k, arr);
      });
      const groups = Array.from(byCliente.entries()).map(([k, arr]) => ({
        id: `grp-${k}`,
        remetenteNome: k,
        isGroup: true,
        children: arr,
      }));
      const cmpCliente = (a, b) => String(a.remetenteNome || '').localeCompare(String(b.remetenteNome || ''));
      const cmpQtd = (a, b) => (a.children?.length || 0) - (b.children?.length || 0);
      groups.sort((a, b) => {
        const base = reimpOrderBy === 'quantidade' ? cmpQtd(a, b) : cmpCliente(a, b);
        return reimpOrder === 'asc' ? base : -base;
      });
      return groups;
    } catch {
      return [];
    }
  }, [reimpressaoRows, reimpOrderBy, reimpOrder]);

  // Ao iniciar página, verificar VeiculoUso.dtInicio vs hoje para o veículo atual
  useEffect(() => {
    const run = async () => {
      try {
        if (buscaPath === 'ssw') {
          setUsageToClose(null);
          return;
        }

        const placaRef = veiculoDoDia?.placa || veiculoDoDia?.veiculo?.placa || onboardData?.vehicleData?.placa;
        if (!placaRef || !user?.codigo) return;
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        const diaLocal = toLocalYYYYMMDD(hoje);
        const placaRefNorm = String(placaRef || '').toUpperCase();

        const usos = await apiService.veiculoUso.list({ placa: placaRef, usuarioCodigo: String(user.codigo) });
        if (Array.isArray(usos) && usos.length > 0) {
          // Encontrar uso em aberto (sem dtFim)
          const aberto = usos.find(u => !u.dtFim);
          if (aberto) {
            // Obter dtInicio (fallback para dtUso)

            let inicioStr = aberto.dtInicio || aberto.dtUso || '';
            const inicioDia = String(inicioStr).substring(0, 10);
            if (inicioDia && inicioDia !== diaLocal) {
              // Uso em aberto de dia diferente: solicitar dtFim
              setUsageToClose(aberto);
            } else {
              setUsageToClose(null);
            }
            // Atualizar status de uso no vehicleData
            updateOnboardData({
              vehicleData: {
                ...(onboardData?.vehicleData || {}),
                statusUso: 'aberto',
                dtInicio: aberto.dtInicio || aberto.dtUso || null,
                dtFim: aberto.dtFim || null,
                placa: placaRefNorm
              }
            });
          } else {
            setUsageToClose(null);
            // Sem uso em aberto para a placa do usuário: marcar como fechado
            updateOnboardData({
              vehicleData: {
                ...(onboardData?.vehicleData || {}),
                statusUso: 'fechado',
                placa: placaRefNorm
              }
            });
          }
        } else {
          setUsageToClose(null);
          // Nenhum uso encontrado: status fechado
          updateOnboardData({
            vehicleData: {
              ...(onboardData?.vehicleData || {}),
              statusUso: 'fechado',
              placa: placaRefNorm
            }
          });
        }
      } catch (e) {
        setUsageToClose(null);
      }
    };

    run();
  }, [veiculoDoDia?.placa, veiculoDoDia?.veiculo?.placa, onboardData?.vehicleData?.placa, user?.codigo]);

  useEffect(() => {
    const run = async () => {
      try {
        if (!user?.codigo) return;
        setVehicleSelectionLoading(true);
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        const diaParam = onboardData?.dtUso || toSpYYYYMMDD(hoje);
        const resp = await apiService.veiculoUso.list({ usuarioCodigo: String(user.codigo), dia: diaParam });
        const usosArr = Array.isArray(resp?.data)
          ? resp.data
          : Array.isArray(resp?.items)
            ? resp.items
            : Array.isArray(resp)
              ? resp
              : [];
        const normalized = usosArr.map((uso) => {
          const placa = uso?.placa ?? uso?.veiculo?.placa ?? uso?.vehiclePlaca ?? null;
          const diaVal = uso?.dtUso ?? uso?.dia ?? uso?.data ?? uso?.date ?? uso?.createdAt ?? null;
          const usuarioCodigo = uso?.usuarioCodigo ?? uso?.user?.codigo ?? uso?.userCodigo ?? null;
          return { ...uso, placa, dia: diaVal, usuarioCodigo };
        });
        setUserUsages(normalized);
        if (!vehicleSelectionShown && normalized.length > 1) {
          //setVehicleSelectionVisible(true);
          setVehicleSelectionShown(true);
        }
      } catch (err) {
      } finally {
        setVehicleSelectionLoading(false);
      }
    };
    run();
  }, [user?.codigo]);

  // Após obter dados do veículo em uso, buscar detalhes do veículo e depois notas
  useEffect(() => {
    const run = async () => {
      try {
        if (notasCarregadas) return;
        const vehicleId = veiculoDoDia?.veiculo?.id || veiculoDoDia?.veiculoId;
        const placaAtual = veiculoDoDia?.placa || veiculoDoDia?.veiculo?.placa || onboardData?.vehicleData?.placa || '';
        if ((!vehicleId && !placaAtual) || !user?.id) return;
        const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
        const diaLocal = toLocalYYYYMMDD(hoje);
        // Buscar detalhes do veículo na tabela 'veiculos' para capacidade
        try {
          let vDetalhes = null;
          if (vehicleId) {
            vDetalhes = await apiService.veiculo.getById(vehicleId);
          } else if (placaAtual) {
            vDetalhes = await apiService.veiculo.getByPlaca(placaAtual);
          }
          if (vDetalhes) {
            const capacidadeIdeal = Number(vDetalhes?.peso || 0);
            const m3Calculado = Number(vDetalhes?.m3 || 0);
            updateOnboardData({
              vehicleData: {
                ...(onboardData?.vehicleData || {}),
                capacidadeIdeal,
                m3Calculado,
                placa: placaAtual || onboardData?.vehicleData?.placa || ''
              }
            });
          }
        } catch (e) { }
        await buscarNotasTotais(vehicleId, diaLocal, user.id);
        setNotasCarregadas(true);
      } catch (e) { }
    };
    run();
  }, [veiculoDoDia?.veiculo?.id, veiculoDoDia?.veiculoId, veiculoDoDia?.placa, user?.id, notasCarregadas]);




  // Filtra/ordena notas do cliente selecionado (usando onboardData.notasDoCliente com fallback para clienteSelecionado.notas)
  const getNotasFiltradas = () => {
    const notas = (onboardData?.notasDoCliente && onboardData.notasDoCliente.length > 0)
      ? onboardData.notasDoCliente
      : (clienteSelecionado?.notas || []);

    // Deduplicar registros pelo ID do vínculo (nvu.id ou similar)
    // Se houver duplicatas, preferir a que tiver cidade preenchida
    const deduplicatedMap = new Map();
    notas.forEach(n => {
      // Identificador único do registro (usando o id do vínculo que vem do backend)
      const id = n?.id || `${n?.nota?.nota}-${n?.nota?.serie}-${n?.nota?.cdremetente}`;
      const existing = deduplicatedMap.get(id);

      const hasCidade = !!(n?.nota?.destinatario?.cidade || n?.destino);

      if (!existing || (!existing.hasCidade && hasCidade)) {
        deduplicatedMap.set(id, { ...n, hasCidade });
      }
    });

    let lista = Array.from(deduplicatedMap.values());

    const getNotaStatus = (n) => {
      try {
        const s = n?.status || n?.nota?.status;
        if (typeof s === 'number') return s;
      } catch { }
      return Number(n?.status || 0);
    };

    if (filtroAtivo && filtroNota.trim()) {
      const q = filtroNota.trim().toLowerCase();
      lista = lista.filter(n => {
        const numero = String(n?.nota?.nota ?? '').toLowerCase();
        const destinatario = String(n?.nota?.destinatario?.nome ?? '').toLowerCase();
        return numero.includes(q) || destinatario.includes(q);
      });
    }
    if (ordenacaoNotas === 'Status') {
      lista.sort((a, b) => getNotaStatus(a) - getNotaStatus(b));
    } else if (ordenacaoNotas === 'Pedido') {
      const getPedido = (n) => String(n?.nota?.pedido || '').toLowerCase();
      lista.sort((a, b) => getPedido(a).localeCompare(getPedido(b)));
    } else if (ordenacaoNotas === 'Praca') {
      const getCidade = (n) => String(n?.nota?.destinatario?.cidade || '').toLowerCase();
      lista.sort((a, b) => getCidade(a).localeCompare(getCidade(b)));
    } else if (ordenacaoNotas === 'CD') {
      const getCD = (n) => String(n?.nota?.cddestinatario ?? n?.nota?.cdremetente ?? '').toLowerCase();
      lista.sort((a, b) => getCD(a).localeCompare(getCD(b)));
    } else if (ordenacaoNotas === 'UF') {
      const getUF = (n) => String(n?.nota?.destinatario?.uf || '').toLowerCase();
      lista.sort((a, b) => getUF(a).localeCompare(getUF(b)));
    } else {
      // Ordenar por número da nota
      const getNumero = (n) => Number(n?.nota?.nota ?? 0);
      lista.sort((a, b) => getNumero(a) - getNumero(b));
    }
    return lista;
  };

  const abrirNotaPorChave = (chave) => {
    try {
      const lista = onboardData?.notasDoCliente || [];
      const key = String(chave || '').trim();
      const encontrada = lista.find(n => String(n?.nota?.chave || '') === key);

      if (encontrada) {
        encontrada.placa = veiculoDoDia?.placa || veiculoDoDia?.veiculo?.placa || onboardData?.vehicleData?.placa || '';
        setNotaAtual(encontrada);
        setNotaEncontradaModal(encontrada);
        setModalVisible(true);
      } else {
        message.warning('Nota não encontrada');
      }
    } catch (error) {
      console.error('Erro em abrirNotaPorChave:', error);
    }
  };


  const enviarParaImpressora = async (notaObj) => {
    try {
      const conectada = (printerConnected === true) || verificaImpressora();
      if (!conectada) {
        setPrinterModalVisible(true);
        return false;
      }
      const filialRaw = user?.assignedBranch?.codigo;
      const filialInt = filialRaw == null ? NaN : parseInt(String(filialRaw), 10);
      const raw = (notaObj && notaObj.nota) ? notaObj.nota : (notaObj || {});
      const ci = (notaObj && notaObj.nota == null) ? notaObj : null;
      const destinatario = {
        nome: String(raw?.destinatario?.nome || ci?.destinatario || clienteSelecionado?.DsEntidade || '').trim(),
        endereco: String(raw?.destinatario?.endereco || '').trim(),
        nro: (raw?.destinatario?.nro ?? ci?.nro ?? ''),
        cidade: String(raw?.destinatario?.cidade || ci?.cidade || raw?.cidade || raw?.nmcidade || raw?.destino || raw?.rota || raw?.cd || clienteSelecionado?.DsCidade || clienteSelecionado?.Cidade || '').trim(),
        uf: String(raw?.destinatario?.uf || ci?.uf || raw?.uf || clienteSelecionado?.DsUF || clienteSelecionado?.UF || '').trim(),
        cnpj: String(raw?.destinatario?.cnpj || ci?.cddestinatario || '').trim(),
      };
      const remetenteNome = String(raw?.remetente?.nome || notaObj?.remetenteNome || '').trim();
      const cnpjrmte = String(raw?.cdremetente || raw?.cnpjrmte || notaObj?.cdremetente || notaObj?.cnpjrmte || notaObj?.nota?.cdremetente || clienteSelecionado?.CdInscricao || '').trim();
      const notaVal = (raw?.nota ?? raw?.numero ?? ci?.nota ?? '');
      const serieVal = (raw?.serie ?? ci?.serie ?? '');
      const cepEntrega = String(raw?.cepentrega || ci?.cep || clienteSelecionado?.CdCep || clienteSelecionado?.Cep || '').trim();
      const nr1Val = raw?.nr1 ?? raw?.detalhe?.nr1 ?? null;
      const nr2Val = raw?.nr2 ?? raw?.detalhe?.nr2 ?? null;
      const volsVal = Number((raw?.volumes ?? raw?.detalhe?.volumes ?? notaObj?.volumes ?? notaObj?.detalhe?.volumes ?? 1) || 1);
      const rotaVal = raw?.rota ?? raw?.detalhe?.rota ?? ci?.rota ?? raw?.destino?.rota ?? raw?.destinatario?.rota ?? '';
      const cdVal = raw?.cd ?? raw?.detalhe?.cd ?? ci?.cd ?? '';
      const setorValRaw = raw?.setor ?? raw?.detalhe?.setor ?? ci?.setor ?? '';
      const setorVal = typeof setorValRaw === 'number' ? setorValRaw : (setorValRaw ? parseInt(String(setorValRaw), 10) : undefined);
      const chaveNfe = String(raw?.chave || raw?.chaveNfe || raw?.chavenfe || notaObj?.chave || notaObj?.chaveNfe || notaObj?.chavenfe || '').trim();
      const isSswFlow = Boolean(raw?.origem === 'ssw' || notaObj?.origem === 'ssw' || raw?.isSsw || notaObj?.isSsw || (chaveNfe && (!nr1Val && !nr2Val)));

      const notaForPrint = {
        ...raw,
        chave: chaveNfe,
        chaveNfe: chaveNfe,
        volumes: volsVal,
        isSsw: isSswFlow,
        origem: isSswFlow ? 'ssw' : (raw?.origem || 'proprio'),
        rota: rotaVal,
        cd: cdVal,
        setor: setorVal,
        remetente: remetenteNome,
        cnpjrmte: cnpjrmte,
        cdremetente: cnpjrmte,
        nro: (raw?.destinatario?.nro ?? ci?.nro ?? ''),
        destinatario,
        nota: notaVal,
        serie: serieVal,
        cepentrega: cepEntrega,
        detalhe: {
          nr1: nr1Val,
          nr2: nr2Val,
          volumes: volsVal,
          rota: rotaVal,
          cd: cdVal,
          setor: setorVal,
        },
      };

      const resolvedPrinter = printerName || printerService?.printerName || (typeof window !== 'undefined' ? localStorage.getItem('printerName') : null) || null;
      const payload = {
        printername: resolvedPrinter,
        impressora: resolvedPrinter,
        usuarioId: user?.id || null,
        organizationId: user?.organizationId || null,
        assignedBranchId: user?.assignedBranchId || null,
        filial: Number.isInteger(filialInt) ? filialInt : undefined,
        chaveNfe: chaveNfe,
        chave: chaveNfe,
        volumes: volsVal,
        isSsw: isSswFlow,
        origem: isSswFlow ? 'ssw' : (raw?.origem || 'proprio'),
        rota: rotaVal || undefined,
        cd: cdVal || undefined,
        setor: setorVal,
        cepentrega: cepEntrega || undefined,
        veiculoId: veiculoDoDia?.veiculo?.id || veiculoDoDia?.veiculoId || onboardData?.vehicleData?.id || null,
        placa: veiculoDoDia?.veiculo?.placa || veiculoDoDia?.placa || onboardData?.vehicleData?.placa || null,
        cdremetente: cnpjrmte,
        cnpjrmte: cnpjrmte,
        nota: notaForPrint,
      };

      const ok = await imprimir(payload);
      if (ok) {
        try {
          if (notaObj) {
            notaObj.status = 1;
            notaObj.Status = 1;
            if (notaObj.nota) {
              notaObj.nota.status = 1;
              notaObj.nota.Status = 1;
            }
          }
        } catch { }
        try {
          setOnboardData(prev => {
            const lista = Array.isArray(prev?.notasDoCliente) ? [...prev.notasDoCliente] : [];
            const getNoteId = (n) => n?.id || `${n?.nota?.nota}-${n?.nota?.serie}-${n?.nota?.cdremetente}`;
            const targetId = getNoteId(notaObj);
            const idx = lista.findIndex(it => getNoteId(it) === targetId);
            if (idx >= 0) {
              const cur = lista[idx] || {};
              const curNota = cur.nota || {};
              lista[idx] = { ...cur, status: 1, Status: 1, nota: { ...curNota, status: 1, Status: 1 } };
            }
            return { ...(prev || {}), notasDoCliente: lista };
          });
        } catch { }

        // Atualiza imediatamente os totais do veículo e dados de coleta-header
        try {
          const vehicleId = veiculoDoDia?.veiculo?.id || veiculoDoDia?.veiculoId || onboardData?.vehicleData?.id;
          const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
          const dtUso = (onboardData?.dtUso ? String(onboardData.dtUso).substring(0, 10) : '') || toLocalYYYYMMDD(hoje);
          if (vehicleId && user?.id) {
            buscarNotasTotais(vehicleId, dtUso, user.id);
          }
        } catch { }

        try {
          window.dispatchEvent(new CustomEvent('etiquetasImpressas', {
            detail: { chave: chaveNfe, volumes: volsVal, reimpressao: false, timestamp: Date.now() }
          }));
        } catch { }

        return true;
      } else {
        try { message.error('Falha ao imprimir etiqueta(s)'); } catch { }
        return false;
      }
    } catch (err) {
      console.error('Erro em enviarParaImpressora:', err);
      return false;
    }
  };

  const imprimirSelecionadas = async (clientInfo) => {
    try {
      const lista = getNotasFiltradas().filter(n => {
        const nf = n?.nota || n || {};
        const impressa = nf.status === 1 || nf.status === '1' || nf.Status === 1 || nf.impressa === 'S' || nf.impressa === true;
        return !impressa;
      });
      if (!lista || lista.length === 0) {
        message.info('Nenhuma nota pendente de impressão encontrada.');
        return;
      }
      let lastItem = null;
      let lastIdx = -1;
      let sucessos = 0;
      for (let i = 0; i < lista.length; i++) {
        if (i > 0 && i % 10 === 0) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
        const ok = await enviarParaImpressora(lista[i]);
        if (!ok) break;
        lista[i].status = 1;
        lista[i].Status = 1;
        if (lista[i].nota) {
          lista[i].nota.status = 1;
          lista[i].nota.Status = 1;
        }
        lastItem = lista[i];
        lastIdx = i;
        sucessos++;
      }

      if (sucessos > 0) {
        message.success(`${sucessos} nota(s) enviada(s) para impressão com sucesso!`);
        try {
          const vehicleId = veiculoDoDia?.veiculo?.id || veiculoDoDia?.veiculoId;
          const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
          const dtUso = (onboardData?.dtUso ? String(onboardData.dtUso).substring(0, 10) : '') || toLocalYYYYMMDD(hoje);
          if (vehicleId && user?.id) {
            buscarNotasTotais(vehicleId, dtUso, user.id);
          }
          const cnpjAlvo = clientInfo?.CdInscricao || clienteSelecionado?.CdInscricao || lastItem?.cdremetente || lastItem?.nota?.cdremetente;
          if (cnpjAlvo) {
            fetchClientNotes(cnpjAlvo);
          }
        } catch { }
      }
    } catch (err) {
      console.error('Erro em imprimirSelecionadas:', err);
    }
  };

  const renderNotaItem = (nota, index) => {
    const n = nota?.nota || {};
    const raw = (nota && nota.nota) ? nota.nota : (nota || {});

    const rawRota = String(raw?.rota ?? raw?.detalhe?.rota ?? raw?.cdrota ?? '').trim();
    const rawCd = String(raw?.cd ?? raw?.detalhe?.cd ?? raw?.cddestinatario ?? raw?.cdremetente ?? raw?.cdfilial ?? '').trim();

    // Extrair cidade válida garantindo que rota/CD não sejam usados como fallback de cidade
    let cidadeResolved = String(
      raw?.destinatario?.cidade ??
      raw?.cidade ??
      raw?.nmcidade ??
      raw?.destino ??
      raw?.detalhe?.cidade ??
      ''
    ).trim();

    // Se o valor resolvido for idêntico à rota ou ao CD, não é um nome de cidade legítimo
    if (cidadeResolved && (cidadeResolved.toUpperCase() === rawRota.toUpperCase() || cidadeResolved.toUpperCase() === rawCd.toUpperCase())) {
      cidadeResolved = '';
    }

    const nf = {
      chave: nota.chave,
      remetenteNome: nota.remetenteNome || n?.remetente?.nome || null,
      nota: raw?.nota ?? raw?.NOTA ?? raw?.nrnota ?? raw?.nrnf ?? raw?.numero,
      nrnota: raw?.nrnota,
      nrnf: raw?.nrnf,
      pedido: raw?.pedido ?? raw?.nrpedido,
      volumes: raw?.volumes ?? raw?.qtvolume ?? raw?.detalhe?.volumes,
      peso: raw?.peso ?? raw?.vlpeso ?? raw?.detalhe?.peso,
      m3: raw?.m3 ?? raw?.vlcubagem ?? raw?.detalhe?.m3,
      destinatario: raw?.destinatario?.nome ?? raw?.destinatario ?? raw?.nmdestinatario,
      destino: cidadeResolved || null,
      uf: raw?.destinatario?.uf ?? raw?.uf ?? raw?.detalhe?.uf ?? '',
      // Preferir campos resolvidos do backend (nota.rota, nota.cd, nota.sigla) e fazer fallback para detalhe
      rota: rawRota || null,
      cd: rawCd || null,
      setor: raw?.setor ?? raw?.detalhe?.setor ?? null,
      nr1: raw?.nr1 ?? raw?.detalhe?.nr1 ?? '',
      nr2: raw?.nr2 ?? raw?.detalhe?.nr2 ?? '',
      status: raw?.status ?? nota?.status
    };

    const impressa = nf.status === 1 || nf.status === '1';
    const notaId = `${nf.nota ?? nf.nrnota ?? nf.nrnf ?? index}`;

    return (
      <div
        key={`nota-${index}`}
        id={`nota-${notaId}`}
        className="nota-item"
        style={{
          border: '1px solid #d9d9d9',
          borderRadius: '6px',
          marginBottom: '12px',
          backgroundColor: '#eaf2ff'
        }}
      >
        {/* Cabeçalho da nota */}
        <div
          className="nota-header"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 12px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Checkbox
              checked={!impressa && selecionarNaoImpressas}
              disabled={impressa}
            />
            <span className="nota-nf" style={{ fontWeight: 'bold' }}>NF: {nf.nota ?? 'N/A'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="nota-ped" style={{ fontSize: 13 }}>PED: {nf.pedido ?? '0'}</span>
            {nf.status === 0 && (
              <Button
                type="text"
                icon={<PrinterOutlined style={{ color: '#ff4d4f' }} />}
                size="small"
                disabled={impressa}
                onClick={(e) => { e?.stopPropagation?.(); enviarParaImpressora(nota); }}
              />
            )}
            <DownOutlined
              style={{ color: '#595959', cursor: 'pointer' }}
              onClick={() => toggleNotaExpansao(notaId)}
            />
          </div>
        </div>

        {/* Conteúdo expandido - controlado por CSS */}
        <div className="nota-expandable-content" onClick={() => {
          try {
            const chave = String(n?.chave || '');
            abrirNotaPorChave(chave);
          } catch (e) {
            console.error('Erro ao clicar na nota:', e);
          }
        }}>
          <>
            <div className="nota-stats" style={{ padding: '0 12px 8px' }}>
              <Row gutter={[16, 8]}>
                <Col span={8}>
                  <div className="nota-stat-label" style={{ color: '#1d4ed8', fontSize: 12 }}>VOL(S)</div>
                  <div className="nota-stat-value" style={{ color: '#1d4ed8', fontWeight: 'bold' }}>{nf.volumes ?? '0'}</div>
                </Col>
                <Col span={8}>
                  <div className="nota-stat-label" style={{ color: '#1d4ed8', fontSize: 12 }}>PESO</div>
                  <div className="nota-stat-value" style={{ color: '#1d4ed8', fontWeight: 'bold' }}>{nf.peso ? Number(nf.peso).toFixed(2) : '0.00'}</div>
                </Col>
                <Col span={8}>
                  <div className="nota-stat-label" style={{ color: '#1d4ed8', fontSize: 12 }}>M3</div>
                  <div className="nota-stat-value" style={{ color: '#1d4ed8', fontWeight: 'bold' }}>{nf.m3 ? Number(nf.m3).toFixed(2) : '0.00'}</div>
                </Col>
              </Row>
            </div>

            <div className="nota-destinatario-container" style={{ padding: '0 12px 8px', fontSize: 12 }}>
              <div className="nota-label" style={{ color: '#666' }}>Destinatário</div>
              <div className="nota-destinatario" style={{ color: '#1d4ed8', fontWeight: 'bold', textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {nf.destinatario ?? 'N/A'}
              </div>
            </div>
          </>

          <div style={{ padding: '0 12px 12px' }}>
            <Row gutter={[16, 8]}>
              <Col span={8}>
                <div style={{ fontSize: 12, color: '#666' }} className="label-responsive">NR1</div>
                <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} className="value-responsive">{nf.nr1 ?? ''}</div>
              </Col>
              <Col span={8}>
                <div style={{ fontSize: 12, color: '#666' }} className="label-responsive">NR2</div>
                <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} className="value-responsive">{nf.nr2 ?? ''}</div>
              </Col>
              <Col span={8}>
                {nf.nr1 && nf.nr2 && impressa ? (
                  <Button
                    type="default"
                    size="small"
                    shape="circle"
                    icon={<PrinterOutlined />}
                    style={{ backgroundColor: '#52c41a', color: '#fff', borderColor: '#52c41a' }}
                    onClick={(e) => {
                      e?.stopPropagation?.();
                      const conectada = (printerConnected === true) || verificaImpressora();
                      if (!conectada) {
                        setReprintError('Impressora não definida');
                        setPrinterModalVisible(true);
                        return;
                      }
                      enviarEtiquetaInicial(nf, nf.nr1, nf.nr2)
                        .then(() => {
                          try { message.success('Etiqueta inicial enviada'); } catch { }
                          try {
                            window.dispatchEvent(new CustomEvent('etiquetasReimpressas', {
                              detail: { chave: nf.chave, reimpressao: true, timestamp: Date.now() }
                            }));
                          } catch { }
                        })
                        .catch(() => { try { message.error('Falha ao enviar etiqueta'); } catch { } });
                    }}
                  />
                ) : null}
              </Col>
            </Row>
          </div>

        </div>

        <div style={{ padding: '0 12px 12px' }} className="linha-inferior-responsive">
          <Row gutter={[16, 8]}>
            <Col span={12}>
              <div style={{ fontSize: 12, color: '#666' }} className="label-responsive">Cidade</div>
              <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} className="value-responsive">
                {(nf.destino ?? 'N/A')}{nf.uf ? ` - ${nf.uf}` : ''}
              </div>
            </Col>
            <Col span={4}>
              <div style={{ fontSize: 12, color: '#666' }} className="label-responsive">ROTA</div>
              <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} className="value-responsive">{nf.rota ?? 'N/A'}</div>
            </Col>
            <Col span={4}>
              <div style={{ fontSize: 12, color: '#666' }} className="label-responsive">SETOR</div>
              <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} className="value-responsive">{nf.setor ?? 'N/A'}</div>
            </Col>
            <Col span={4}>
              <div style={{ fontSize: 12, color: '#666' }} className="label-responsive">C.D</div>
              <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} className="value-responsive">{nf.cd ?? 'N/A'}</div>
            </Col>
          </Row>
        </div>
      </div>
    );
  };

  // Verificar se os dados estão carregados (sem lógica de romaneio)
  if (!isAuthenticated || loading) {
    const loadingMessage = "Carregando dados do veículo...";
    const loadingDescription = "Buscando uso do veículo, detalhes e notas.";

    return (
      <div className="min-h-screen with-navbar">
        <Navbar onMenuClick={() => { }} />
        <div className="onboard-container">
          <Alert
            message={loadingMessage}
            description={loadingDescription}
            type="info"
            showIcon
          />
        </div>
      </div>
    );
  }

  // Verificar se há erro
  if (error) {
    return (
      <div className="min-h-screen with-navbar">
        <Navbar onMenuClick={() => { }} />
        <div className="onboard-container">
          <Alert
            message="Erro ao carregar dados"
            description={error}
            type="error"
            showIcon
          />
        </div>
      </div>
    );
  }


  // Usar dados carregados do contexto (sem depender de romaneio)
  const dadosAtivos = {
    romaneioInfo,
    userInfo,
    vehicleData,
    empresasData,
    totals,
    coletas
  };

  // Percentuais de uso de capacidade (peso e m3) com arredondamento e limite
  const capacidadePeso = Number(veiculoDoDia?.veiculo?.peso ?? 0) * 1000;
  const usadoPeso = Number(dadosAtivos?.totals?.peso || 0);
  const pesoPercent = capacidadePeso > 0 ? Math.min(100, (usadoPeso / capacidadePeso) * 100) : 0;

  const capacidadeM3 = Number(dadosAtivos?.vehicleData?.m3Calculado || 0);
  const usadoM3 = Number(dadosAtivos?.totals?.m3 || 0);
  const m3Percent = capacidadeM3 > 0 ? Math.min(100, (usadoM3 / capacidadeM3) * 100) : 0;

  // Funções da modal
  const handleOpenModal = () => {
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
  };



  // Ouve evento global de leitura de código de barras e abre a modal
  // (movido para cima para manter ordem dos hooks)

  return (
    <>
      {/* Navbar Fixa */}
      <Navbar onMenuClick={() => { }} />
      <div className="page-layout">
        {/* Main Content */}
        <main className="page-main">
          <div className="onboard-container">
            {/* Seção Totais do Veículo */}
            <Card
              title={(function () {
                const placaTitulo = veiculoDoDia?.placa || veiculoDoDia?.veiculo?.placa || dadosAtivos.vehicleData?.placa;
                return `Totais do Veículo ${placaTitulo ? ` - ${placaTitulo}` : ''}`;
              })()}
              className="totals-card"
              size="small"
              extra={(() => {
                // Ícone de caminhão com comportamento condicional
                const aberto = !!veiculoDoDia && !veiculoDoDia.dtFim;
                const toSpYYYYMMDD = (date) => {
                  try {
                    const d = new Date(date);
                    const parts = new Intl.DateTimeFormat('sv-SE', {
                      timeZone: 'America/Sao_Paulo',
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    }).formatToParts(d);
                    const get = (t) => parts.find((p) => p.type === t)?.value || '00';
                    return `${get('year')}-${get('month')}-${get('day')}`;
                  } catch {
                    return '';
                  }
                };
                const hojeSp = toSpYYYYMMDD(new Date());
                const dtFimStr = veiculoDoDia?.dtFim || null;
                const dtFimSp = dtFimStr ? toSpYYYYMMDD(dtFimStr) : '';
                const isClosedToday = !!veiculoDoDia && !aberto && !!dtFimSp && (dtFimSp === hojeSp);

                const handleEncerrar = () => {
                  if (veiculoDoDia?.id) {
                    setUsageToClose(veiculoDoDia);
                  } else {
                    setModalPlacaVisible(true);
                  }
                };

                const handleReabrir = async () => {
                  try {
                    if (!veiculoDoDia?.id) {
                      setModalPlacaVisible(true);
                      return;
                    }
                    const hoje = new Date();
                    hoje.setHours(0, 0, 0, 0);
                    const diaLocal = toLocalYYYYMMDD(hoje);
                    const diaParam = onboardData?.dtUso || toSpYYYYMMDD(new Date());
                    if (user?.codigo) {
                      setVehicleSelectionLoading(true);
                      try {
                        const resp = await apiService.veiculoUso.list({ usuarioCodigo: String(user.codigo), dia: diaParam });
                        const usosArr = Array.isArray(resp?.data)
                          ? resp.data
                          : Array.isArray(resp?.items)
                            ? resp.items
                            : Array.isArray(resp)
                              ? resp
                              : [];
                        const normalized = usosArr.map((uso) => {
                          const placa = uso?.placa ?? uso?.veiculo?.placa ?? uso?.vehiclePlaca ?? null;
                          const diaVal = uso?.dtUso ?? uso?.dia ?? uso?.data ?? uso?.date ?? uso?.createdAt ?? null;
                          const usuarioCodigo = uso?.usuarioCodigo ?? uso?.user?.codigo ?? uso?.userCodigo ?? null;
                          return { ...uso, placa, dia: diaVal, usuarioCodigo };
                        });
                        setUserUsages(normalized);
                        if (normalized.length > 1) {
                          setVehicleSelectionVisible(true);
                          return;
                        }
                      } catch { }
                      finally {
                        setVehicleSelectionLoading(false);
                      }
                    }
                    await apiService.veiculoUso.update(veiculoDoDia.id, { dtFim: null });
                    setVeiculoDoDia({ ...veiculoDoDia, dtFim: null });
                    message.success('Coletas reabertas para hoje.');
                  } catch (e) {
                    const msg = String(e?.message || '').toLowerCase();
                    const rejectsDtFim = msg.includes('dtfim') && msg.includes('should not exist');
                    if (rejectsDtFim) {
                      message.error('Servidor não aceita reabrir via dtFim: null. Atualize o backend para suportar dtFim opcional.');
                    } else {
                      message.error(e?.message || 'Falha ao reabrir coletas');
                    }
                  }
                };

                let color = '#d9d9d9';
                let onClick = () => setModalPlacaVisible(true);
                let tooltip = 'Selecionar veículo';

                if (aberto) {
                  color = '#52c41a';
                  onClick = handleEncerrar;
                  tooltip = 'Encerrar Coletas';
                } else if (isClosedToday) {
                  color = '#faad14';
                  onClick = handleReabrir;
                  tooltip = 'Reabrir';
                }

                return (
                  <Space size="small">
                    <Tooltip title={tooltip} placement="top">
                      <Button
                        size="small"
                        onClick={onClick}
                        style={{ color: color, borderColor: color }}
                        icon={aberto ? <TruckOutlined /> : <TruckOutlined style={{ transform: 'scaleX(-1)' }} />}
                      />
                    </Tooltip>
                    <Typography.Text
                      onClick={onClick}
                      style={{ color: color, fontSize: 'clamp(11px, 2.6vw, 13px)', cursor: 'pointer' }}
                    >
                      {tooltip}
                    </Typography.Text>
                    {isClosedToday && (
                      <Space size="small">
                        <Tag color="gold">Fechado</Tag>
                        <Button
                          size="small"
                          type="dashed"
                          icon={<PlusOutlined />}
                          onClick={() => { setUsageToClose(null); setModalPlacaVisible(true); }}
                        >
                          <span className="btn-vincular-text">Vincular veículo</span>
                        </Button>
                      </Space>
                    )}
                  </Space>
                );
              })()}
            >
              <Row gutter={[8, 8]} style={{ flexWrap: 'nowrap' }}>
                <Col flex="1" style={{ minWidth: '60px' }}>
                  <Statistic title="Notas" value={dadosAtivos.totals.notas} />
                </Col>
                <Col flex="1" style={{ minWidth: '60px' }}>
                  <Statistic title="Vols" value={dadosAtivos.totals.vols} />
                </Col>
                <Col flex="2" style={{ minWidth: '80px' }}>
                  <Statistic title="Peso" value={dadosAtivos.totals.peso} precision={2} />
                </Col>
                <Col flex="1" style={{ minWidth: '60px' }}>
                  <Statistic title="M3" value={dadosAtivos.totals.m3} precision={2} />
                </Col>
                <Col flex="1" style={{ minWidth: '60px' }}>
                  <Statistic
                    title="Falta"
                    value={dadosAtivos.totals.falta}
                    precision={0}
                    className={Number(dadosAtivos?.totals?.falta || 0) > 0 ? 'stat-blink-red' : ''}
                  />
                </Col>
              </Row>
              <div className="progress-section">
                <Row gutter={[8, 8]} align="middle">
                  <Col flex="none" style={{ width: '60px' }} className="progress-label">
                    Peso
                  </Col>
                  <Col flex="auto">
                    <Progress
                      percent={pesoPercent}
                      showInfo={false}
                      strokeColor="#52c41a"
                      size={8}
                    />
                  </Col>
                  <Col flex="none" style={{ width: '50px' }} className="progress-percentage">
                    {pesoPercent.toFixed(2)}%
                  </Col>
                </Row>
                <Row gutter={[8, 8]} align="middle">
                  <Col flex="none" style={{ width: '60px' }} className="progress-label">
                    M3
                  </Col>
                  <Col flex="auto">
                    <Progress
                      percent={m3Percent}
                      showInfo={false}
                      strokeColor="#52c41a"
                      size={8}
                    />
                  </Col>
                  <Col flex="none" style={{ width: '50px' }} className="progress-percentage">
                    {m3Percent.toFixed(2)}%
                  </Col>
                </Row>
              </div>
            </Card>

            {/* Seção de Tabs */}
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              className="onboard-tabs"
              type="card"
              destroyOnHidden

              items={[
                {
                  key: 'coletas',
                  label: 'Coletas',
                  children: (
                    <div style={{ position: 'relative', minHeight: 220 }}>
                      <div className="tab-content coletas-scroll">
                        {coletas && coletas.length > 0 ? (
                          coletas.map((item, index) => (
                            <div key={item.cdremetente || index} className="coleta-section" style={{ marginBottom: '20px', cursor: 'pointer' }}>
                              {/* Header da Coleta */}
                              <div className="coleta-header" style={{
                                backgroundColor: 'yellow',
                                padding: '8px 12px',
                                borderRadius: '4px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '8px',
                                minHeight: '32px'
                              }}
                                onClick={() => {
                                  const cnpjCliente = item.cdremetente || item.CdInscricao;
                                  const notasCliente = Array.isArray(item.notas) ? item.notas : [];
                                  selecionarCliente({ NrColeta: item.NrColeta || index + 1, DsEntidade: item.remetenteNome || item.DsEntidade, CdInscricao: cnpjCliente, notas: notasCliente });
                                  setActiveTab('notas');
                                  fetchClientNotes(cnpjCliente, notasCliente);
                                }}
                              >
                                <span style={{
                                  fontWeight: 'bold',
                                  fontSize: '14px',
                                  flex: '0 0 auto',
                                  marginRight: '8px'
                                }}>
                                  {String(item.NrColeta || '').replace(/^0+/, '') || index + 1}
                                </span>
                                <div style={{
                                  flex: '1 1 auto',
                                  textAlign: 'right',
                                  overflow: 'hidden',
                                  marginLeft: '8px'
                                }}>
                                  <strong style={{
                                    fontSize: '12px',
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {item.remetenteNome && item.remetenteNome.length > 40
                                      ? `${item.remetenteNome.substring(0, 40)}...`
                                      : item.remetenteNome}
                                  </strong>
                                  {item.CdInscricao && (
                                    <div style={{ fontSize: '10px', color: '#555' }}>
                                      CNPJ: {item.CdInscricao}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Seção NOTAS IMPRESSAS */}
                              {Number(item.notasImpressas || 0) > 0 && (
                                <div className="notas-impressas-section" style={{ marginBottom: '12px' }}>
                                  <div style={{
                                    backgroundColor: '#0a0303ff',
                                    color: 'white',
                                    padding: '4px 8px',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    textAlign: 'center'
                                  }}>
                                    NOTAS IMPRESSAS
                                  </div>
                                  <table style={{ width: '100%', border: '1px solid #d9d9d9', borderCollapse: 'collapse', cursor: 'pointer' }}
                                    onClick={() => {
                                      const cnpjCliente = item.cdremetente || item.CdInscricao;
                                      const notasCliente = Array.isArray(item.notas) ? item.notas : [];
                                      selecionarCliente({ NrColeta: item.NrColeta || index + 1, DsEntidade: item.remetenteNome || item.DsEntidade, CdInscricao: cnpjCliente, notas: notasCliente });
                                      setActiveTab('notas');
                                      fetchClientNotes(cnpjCliente, notasCliente);
                                      setSelecionarNaoImpressas(false);
                                      setOrdenacaoNotas('Nota');
                                    }}
                                  >
                                    <thead
                                      style={{ cursor: 'pointer' }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const cnpjCliente = item.cdremetente || item.CdInscricao;
                                        const notasCliente = Array.isArray(item.notas) ? item.notas : [];
                                        selecionarCliente({ NrColeta: item.NrColeta || index + 1, DsEntidade: item.remetenteNome || item.DsEntidade, CdInscricao: cnpjCliente, notas: notasCliente });
                                        setActiveTab('notas');
                                        fetchClientNotes(cnpjCliente, notasCliente);
                                      }}
                                    >
                                      <tr style={{ backgroundColor: '#000000', color: 'white' }}>
                                        <th style={{ border: '1px solid #d9d9d9', padding: '4px', textAlign: 'center', fontSize: '11px' }}>NOTAS</th>
                                        <th style={{ border: '1px solid #d9d9d9', padding: '4px', textAlign: 'center', fontSize: '11px' }}>VOLS</th>
                                        <th style={{ border: '1px solid #d9d9d9', padding: '4px', textAlign: 'center', fontSize: '11px' }}>PESO</th>
                                        <th style={{ border: '1px solid #d9d9d9', padding: '4px', textAlign: 'center', fontSize: '11px' }}>M3</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr>
                                        <td style={{ border: '1px solid #d9d9d9', padding: '4px', textAlign: 'center', fontSize: '11px' }}>{Number(item.notasImpressas || 0)}</td>
                                        <td style={{ border: '1px solid #d9d9d9', padding: '4px', textAlign: 'center', fontSize: '11px' }}>{Number(item.volumesImpressosTotal || 0)}</td>
                                        <td style={{ border: '1px solid #d9d9d9', padding: '4px', textAlign: 'center', fontSize: '11px' }}>{Number((typeof item.pesoImpressoTotal === 'string' ? parseFloat(item.pesoImpressoTotal) : (item.pesoImpressoTotal || 0)).toFixed ? (typeof item.pesoImpressoTotal === 'string' ? parseFloat(item.pesoImpressoTotal) : (item.pesoImpressoTotal || 0)).toFixed(2) : Number(item.pesoImpressoTotal || 0).toFixed(2))}</td>
                                        <td style={{ border: '1px solid #d9d9d9', padding: '4px', textAlign: 'center', fontSize: '11px' }}>{Number((typeof item.m3ImpressoTotal === 'string' ? parseFloat(item.m3ImpressoTotal) : (item.m3ImpressoTotal || 0)).toFixed ? (typeof item.m3ImpressoTotal === 'string' ? parseFloat(item.m3ImpressoTotal) : (item.m3ImpressoTotal || 0)).toFixed(2) : Number(item.m3ImpressoTotal || 0).toFixed(2))}</td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              )}

                              {/* Seção NÃO IMPRESSAS */}
                              {Number(item.notasNaoImpressas || 0) > 0 && (
                                <div className="notas-nao-impressas-section" style={{ marginBottom: '8px' }}>
                                  <div style={{
                                    backgroundColor: '#ff4d4f',
                                    color: 'white',
                                    padding: '4px 8px',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    textAlign: 'center'
                                  }}>
                                    NOTAS NÃO IMPRESSAS
                                  </div>
                                  <table style={{ width: '100%', border: '1px solid #d9d9d9', borderCollapse: 'collapse', cursor: 'pointer' }}
                                    onClick={() => {
                                      const cnpjCliente = item.cdremetente || item.CdInscricao;
                                      const notasCliente = Array.isArray(item.notas) ? item.notas : [];
                                      selecionarCliente({ NrColeta: item.NrColeta || index + 1, DsEntidade: item.remetenteNome || item.DsEntidade, CdInscricao: cnpjCliente, notas: notasCliente });
                                      setActiveTab('notas');
                                      fetchClientNotes(cnpjCliente, notasCliente);
                                      setSelecionarNaoImpressas(true);
                                      setOrdenacaoNotas('Status');
                                    }}
                                  >
                                    <thead
                                      style={{ cursor: 'pointer' }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const cnpjCliente = item.cdremetente || item.CdInscricao;
                                        const notasCliente = Array.isArray(item.notas) ? item.notas : [];
                                        selecionarCliente({ NrColeta: item.NrColeta || index + 1, DsEntidade: item.remetenteNome || item.DsEntidade, CdInscricao: cnpjCliente, notas: notasCliente });
                                        setActiveTab('notas');
                                        fetchClientNotes(cnpjCliente, notasCliente);
                                      }}
                                    >
                                      <tr style={{ backgroundColor: '#ff4d4f', color: 'white' }}>
                                        <th style={{ border: '1px solid #d9d9d9', padding: '4px', textAlign: 'center', fontSize: '11px' }}>NOTAS</th>
                                        <th style={{ border: '1px solid #d9d9d9', padding: '4px', textAlign: 'center', fontSize: '11px' }}>VOLS</th>
                                        <th style={{ border: '1px solid #d9d9d9', padding: '4px', textAlign: 'center', fontSize: '11px' }}>PESO</th>
                                        <th style={{ border: '1px solid #d9d9d9', padding: '4px', textAlign: 'center', fontSize: '11px' }}>M3</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr>
                                        <td style={{ border: '1px solid #d9d9d9', padding: '4px', textAlign: 'center', fontSize: '11px' }}>{Number(item.notasNaoImpressas || 0)}</td>
                                        <td style={{ border: '1px solid #d9d9d9', padding: '4px', textAlign: 'center', fontSize: '11px' }}>{Number(item.volumesNaoImpressosTotal || 0)}</td>
                                        <td style={{ border: '1px solid #d9d9d9', padding: '4px', textAlign: 'center', fontSize: '11px' }}>{Number((typeof item.pesoNaoImpressoTotal === 'string' ? parseFloat(item.pesoNaoImpressoTotal) : (item.pesoNaoImpressoTotal || 0)).toFixed ? (typeof item.pesoNaoImpressoTotal === 'string' ? parseFloat(item.pesoNaoImpressoTotal) : (item.pesoNaoImpressoTotal || 0)).toFixed(2) : Number(item.pesoNaoImpressoTotal || 0).toFixed(2))}</td>
                                        <td style={{ border: '1px solid #d9d9d9', padding: '4px', textAlign: 'center', fontSize: '11px' }}>{Number((typeof item.m3NaoImpressoTotal === 'string' ? parseFloat(item.m3NaoImpressoTotal) : (item.m3NaoImpressoTotal || 0)).toFixed ? (typeof item.m3NaoImpressoTotal === 'string' ? parseFloat(item.m3NaoImpressoTotal) : (item.m3NaoImpressoTotal || 0)).toFixed(2) : Number(item.m3NaoImpressoTotal || 0).toFixed(2))}</td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          !coletasLoading && (
                            <div style={{ textAlign: 'center', padding: '20px', color: coletasMensagem ? '#d46b08' : '#999' }}>
                              {coletasMensagem || 'Nenhuma coleta encontrada'}
                            </div>
                          )
                        )}
                      </div>
                      {coletasLoading && (
                        <div
                          style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            background: 'rgba(255, 255, 255, 0.55)',
                            zIndex: 10,
                          }}
                        >
                          <PrinterLoader size={110} label="Buscando coletas no ERP..." />
                        </div>
                      )}
                    </div>
                  )
                },
                {
                  key: 'notas',
                  label: 'Notas',
                  children: (
                    <>
                      {/* Nome do cliente selecionado + botão de inclusão de notas */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 8px 0 8px' }}>
                        <div className="cliente-selecionado-nome" style={{ marginBottom: 0, flex: 1 }}>
                          {clienteSelecionado ? (clienteSelecionado?.DsEntidade || '') : 'Nenhum cliente selecionado'}
                        </div>
                        <Button
                          type="primary"
                          shape="circle"
                          icon={<PlusOutlined />}
                          onClick={() => setModalVisible(true)}
                          title="Incluir notas"
                        />
                      </div>

                      <div className="tab-content reimpressoes-content">
                        {clienteSelecionado ? (
                          <div>

                            {/* Container com scroll a partir do Layout de duas colunas */}
                            <div className="scroll-container-notas">
                              {/* Layout de duas colunas */}
                              <Row gutter={16} className="layout-duas-colunas">
                                {/* Coluna esquerda - Select de ordenação */}
                                <Col span={12}>
                                  <Select
                                    value={ordenacaoNotas}
                                    onChange={setOrdenacaoNotas}
                                    style={{ width: '100%' }}
                                    placeholder="Ordenar por"
                                  >
                                    <Select.Option value="Nota">Nota</Select.Option>
                                    <Select.Option value="Pedido">Pedido</Select.Option>
                                    <Select.Option value="Praca">Praça</Select.Option>
                                    <Select.Option value="CD">C.D</Select.Option>
                                    <Select.Option value="UF">UF</Select.Option>
                                    <Select.Option value="Status">Não impressas</Select.Option>
                                  </Select>
                                </Col>

                                {/* Coluna direita - Input group de filtro */}
                                <Col span={12}>
                                  <Space.Compact style={{ width: '100%' }}>
                                    <Input
                                      style={{ flex: 1, backgroundColor: '#f8f9fa' }}
                                      placeholder="Número da nota"
                                      value={filtroNota}
                                      onChange={(e) => setFiltroNota(e.target.value)}
                                    />
                                    <Button
                                      type="default"
                                      icon={<SearchOutlined />}
                                      title="Buscar"
                                      onClick={() => {
                                        // Implementar busca
                                        console.log('Buscar nota:', filtroNota);
                                      }}
                                    />
                                    <Button
                                      type="default"
                                      icon={<FilterOutlined />}
                                      title="Filtrar"
                                      onClick={() => {
                                        setFiltroAtivo(true);
                                        console.log('Filtrar por:', ordenacaoNotas, filtroNota);
                                      }}
                                    />
                                    {filtroAtivo && (
                                      <Button
                                        type="default"
                                        icon={<CloseOutlined />}
                                        title="Remover filtro"
                                        onClick={() => {
                                          setFiltroAtivo(false);
                                          setFiltroNota('');
                                          console.log('Remover filtro');
                                        }}
                                      />
                                    )}
                                  </Space.Compact>
                                </Col>
                              </Row>

                              {/* Checkbox e botão de impressão */}
                              <div className="checkbox-impressao-container">
                                <Checkbox
                                  checked={selecionarNaoImpressas}
                                  onChange={(e) => setSelecionarNaoImpressas(e.target.checked)}
                                  style={{ fontSize: '14px' }}
                                >
                                  Selecionar Notas não impressas
                                </Checkbox>
                                <Button
                                  type="primary"
                                  icon={<PrinterOutlined />}
                                  onClick={() => {
                                    const notas = getNotasFiltradas();
                                    const first = Array.isArray(notas) && notas.length > 0 ? notas[0] : null;
                                    const info = clienteSelecionado ? {
                                      DsEntidade: clienteSelecionado?.DsEntidade,
                                      CdInscricao: clienteSelecionado?.CdInscricao,
                                      NrColeta: 1
                                    } : {
                                      DsEntidade: first?.remetenteNome || first?.nota?.remetente?.nome || '',
                                      CdInscricao: first?.nota?.cdremetente || '',
                                      NrColeta: 1
                                    };
                                    imprimirSelecionadas(info);
                                  }}
                                  style={{
                                    backgroundColor: '#6c757d',
                                    borderColor: '#6c757d',
                                    width: '40px',
                                    height: '40px'
                                  }}
                                />
                              </div>

                              {/* Lista de notas */}
                              <div>
                                {(() => {
                                  const notasFiltradas = getNotasFiltradas();
                                  return notasFiltradas.length > 0 ? (
                                    notasFiltradas.map((nota, index) => renderNotaItem(nota, index))
                                  ) : (
                                    <div style={{
                                      padding: '40px',
                                      textAlign: 'center',
                                      color: '#999',
                                      border: '1px dashed #d9d9d9',
                                      borderRadius: '6px'
                                    }}>
                                      {ordenacaoNotas === 'Status' ?
                                        'Nenhuma nota não impressa encontrada para este cliente' :
                                        'Nenhuma nota encontrada para este cliente'
                                      }
                                      <br />
                                      <small>CNPJ: {clienteSelecionado.CdInscricao}</small>
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div style={{
                            textAlign: 'center',
                            padding: '40px',
                            color: '#999'
                          }}>
                            Selecione um cliente na aba "Coletas" para visualizar suas notas
                          </div>
                        )}
                      </div>
                    </>
                  )
                },
                {
                  key: 'alteracoes',
                  label: 'Alterações',
                  children: (
                    <div className="tab-content reimpressoes-content">
                      {alteracoesLoading ? (
                        <p>Carregando alterações...</p>
                      ) : alteracoesError ? (
                        <Alert type="error" message={alteracoesError} />
                      ) : alteracoesRows && alteracoesRows.length > 0 ? (
                        <Table
                          size="small"
                          pagination={{ pageSize: 8 }}
                          rowKey={(r) => String(r.id || r.noteKey || r.chave || Math.random())}
                          dataSource={alteracoesGrouped}
                          expandable={{
                            rowExpandable: (record) => Array.isArray(record.children) && record.children.length > 0,
                          }}
                          columns={[
                            {
                              title: 'Remetente',
                              key: 'remetente',
                              render: (_, record) => (
                                record.isGroup
                                  ? (
                                    <Space>
                                      <span>{record.remetenteNome || ''}</span>
                                      <Tag color="blue">{record.children?.length || 0}</Tag>
                                    </Space>
                                  )
                                  : ''
                              ),
                            },
                            {
                              title: 'Nota',
                              dataIndex: 'nota',
                              key: 'nota',
                              render: (v, record) => (
                                record.isGroup
                                  ? ''
                                  : record.isNoteGroup
                                    ? (
                                      <Space>
                                        <span>{record.nota}</span>
                                        <Tag color="geekblue">{record.children?.length || 0}</Tag>
                                      </Space>
                                    )
                                    : v
                              ),
                            },
                            {
                              title: 'Série',
                              dataIndex: 'serie',
                              key: 'serie',
                              render: (v, record) => (record.isGroup ? '' : record.isNoteGroup ? record.serie : v),
                            },
                            {
                              title: 'Data',
                              dataIndex: 'dataOperacao',
                              key: 'dataOperacao',
                              render: (v, record) => (!record.isGroup && !record.isNoteGroup ? (v ? dayjs(v).format('DD/MM HH:mm') : '') : ''),
                            },
                            {
                              title: 'Campo',
                              dataIndex: 'campo',
                              key: 'campo',
                              render: (v, record) => (!record.isGroup && !record.isNoteGroup ? v : ''),
                            },
                            {
                              title: 'Anterior',
                              dataIndex: 'valorAnterior',
                              key: 'valorAnterior',
                              render: (v, record) => (!record.isGroup && !record.isNoteGroup ? v : ''),
                            },
                            {
                              title: 'Novo',
                              dataIndex: 'valorNovo',
                              key: 'valorNovo',
                              render: (v, record) => (!record.isGroup && !record.isNoteGroup ? <Tag color="blue">{String(v || '')}</Tag> : ''),
                            },
                          ]}
                        />
                      ) : (
                        <p>Nenhuma alteração registrada.</p>
                      )}
                    </div>
                  )
                },
                {
                  key: 'reimpressoes',
                  label: 'Reimpressões',
                  children: (
                    <div className="tab-content reimpressoes-content">
                      <Space style={{ marginBottom: 12 }}>
                        <Select
                          size="small"
                          value={reimpOrderBy}
                          onChange={setReimpOrderBy}
                          options={[
                            { label: 'Ordenar por Cliente', value: 'cliente' },
                            { label: 'Ordenar por Quantidade', value: 'quantidade' },
                          ]}
                          style={{ width: 200 }}
                        />
                        <Select
                          size="small"
                          value={reimpOrder}
                          onChange={setReimpOrder}
                          options={[
                            { label: 'Ascendente', value: 'asc' },
                            { label: 'Descendente', value: 'desc' },
                          ]}
                          style={{ width: 160 }}
                        />
                      </Space>
                      {reimpressaoLoading ? (
                        <p>Carregando reimpressões...</p>
                      ) : reimpressaoError ? (
                        <Alert type="error" message="Falha ao carregar reimpressões" />
                      ) : reimpressaoRows && reimpressaoRows.length > 0 ? (
                        <Table
                          size="small"
                          pagination={false}
                          rowKey={(r) => r.id || `${r.chave}-${r.volume}`}
                          dataSource={reimpressaoGrouped}
                          expandable={{
                            rowExpandable: (record) => Array.isArray(record.children) && record.children.length > 0,
                          }}
                          columns={[
                            {
                              title: 'Cliente',
                              dataIndex: 'remetenteNome',
                              key: 'remetenteNome',
                              render: (_, record) => (
                                record.isGroup
                                  ? (
                                    <Space>
                                      <span>{record.remetenteNome || record.cnpj || ''}</span>
                                      <Tag color="blue">{record.children?.length || 0}</Tag>
                                    </Space>
                                  )
                                  : ''
                              ),
                            },
                            {
                              title: 'Data',
                              dataIndex: 'data',
                              key: 'data',
                              render: (v, record) => (record.isGroup ? '' : (v ? dayjs(v).format('DD-MM HH:mm') : '')),
                            },
                            {
                              title: 'Nota',
                              dataIndex: 'nota',
                              key: 'nota',
                              render: (v, record) => (record.isGroup ? '' : v),
                            },
                            {
                              title: 'Série',
                              dataIndex: 'serie',
                              key: 'serie',
                              render: (v, record) => (record.isGroup ? '' : v),
                            },
                            {
                              title: 'Volume',
                              dataIndex: 'volume',
                              key: 'volume',
                              render: (v, record) => (record.isGroup ? '' : v),
                            },
                          ]}
                        />
                      ) : (
                        <p>Nenhuma reimpressão registrada.</p>
                      )}
                    </div>
                  )
                },
                {
                  key: 'menu',
                  label: (
                    <Dropdown
                      menu={{
                        items: menuItems,
                        onClick: ({ key }) => {
                          if (key === '1') {
                            setEncerrarModalVisible(true);
                          } else if (key === '2') {
                            setCdModalVisible(true);
                          } else if (key === '3') {
                            setRoteirizacaoModalVisible(true);
                          }
                        }
                      }}
                      trigger={['click']}
                      placement="bottomRight"
                    >
                      <Button type="text" icon={<MoreOutlined />} />
                    </Dropdown>
                  ),
                  children: (
                    <div className="tab-content" id="aba-produtividade" >
                      <Row gutter={[16, 16]} style={{ marginTop: '4px' }}>
                        <Col xs={12} sm={8} md={6}>
                          <Card size="small" className="productivity-card">
                            <Statistic
                              title="Notas (com impressão)"
                              value={totals?.notasImpressas || 0}
                              valueStyle={{ color: '#3f8600' }}
                            />
                          </Card>
                        </Col>
                        <Col xs={12} sm={8} md={6}>
                          <Card size="small" className="productivity-card">
                            <Statistic
                              title="Notas (sem impressão)"
                              value={totals?.notasNaoImpressas || 0}
                              valueStyle={{ color: '#cf1322' }}
                            />
                          </Card>
                        </Col>
                        <Col xs={12} sm={8} md={6}>
                          <Card size="small" className="productivity-card">
                            <Statistic
                              title="Etiquetas (Impressas)"
                              value={totals?.etiquetasImpressas || 0}
                              valueStyle={{ color: '#3f8600' }}
                            />
                          </Card>
                        </Col>
                        <Col xs={12} sm={8} md={6}>
                          <Card size="small" className="productivity-card">
                            <Statistic
                              title="Etiquetas (Não Impressas)"
                              value={totals?.etiquetasNaoImpressas || 0}
                              valueStyle={{ color: '#cf1322' }}
                            />
                          </Card>
                        </Col>
                        <Col xs={12} sm={8} md={6}>
                          <Card size="small" className="productivity-card">
                            <Statistic
                              title="Clientes"
                              value={coletas?.length || 0}
                            />
                          </Card>
                        </Col>
                        <Col xs={12} sm={8} md={6}>
                          <Card size="small" className="productivity-card">
                            <Statistic
                              title="Primeira etiqueta"
                              value={formatarDataHora(totals?.primeiraEtiqueta)}
                              valueStyle={{ fontSize: '14px' }}
                            />
                          </Card>
                        </Col>
                        <Col xs={12} sm={8} md={6}>
                          <Card size="small" className="productivity-card">
                            <Statistic
                              title="Última etiqueta"
                              value={formatarDataHora(totals?.ultimaEtiqueta)}
                              valueStyle={{ fontSize: '14px' }}
                            />
                          </Card>
                        </Col>
                      </Row>

                      <div style={{ marginTop: '24px' }}>
                        <Table
                          id="tb-produtividade"
                          size="small"
                          pagination={false}
                          dataSource={coletas.map(coleta => {
                            let primeira = null;
                            let ultima = null;

                            if (coleta.notas && coleta.notas.length > 0) {
                              coleta.notas.forEach(nota => {
                                if (nota.primeira_etiqueta) {
                                  const d = nota.primeira_etiqueta;
                                  if (!primeira || String(d) < String(primeira)) primeira = d;
                                }
                                if (nota.ultima_etiqueta) {
                                  const d = nota.ultima_etiqueta;
                                  if (!ultima || String(d) > String(ultima)) ultima = d;
                                }
                              });
                            }

                            return {
                              key: coleta.CdInscricao || coleta.NrColeta || coleta.cdremetente,
                              cliente: coleta.remetenteNome || coleta.DsEntidade || coleta.dsremetente || 'Cliente não identificado',
                              volumes: Number(coleta.volumesTotal || 0),
                              peso: Number(typeof coleta.pesoTotal === 'string' ? parseFloat(coleta.pesoTotal) : (coleta.pesoTotal || 0)).toFixed(2),
                              m3: Number(typeof coleta.m3Total === 'string' ? parseFloat(coleta.m3Total) : (coleta.m3Total || 0)).toFixed(3),
                              primeiraEtiqueta: formatarDataHora(primeira),
                              ultimaEtiqueta: formatarDataHora(ultima)
                            };
                          })}
                          columns={[
                            {
                              title: 'Cliente',
                              dataIndex: 'cliente',
                              key: 'cliente',
                            },
                            {
                              title: 'Vols',
                              dataIndex: 'volumes',
                              key: 'volumes',
                              align: 'center',
                            },
                            {
                              title: 'Peso',
                              dataIndex: 'peso',
                              key: 'peso',
                              align: 'right',
                            },
                            {
                              title: 'M3',
                              dataIndex: 'm3',
                              key: 'm3',
                              align: 'right',
                            },
                            {
                              title: 'Primeira',
                              dataIndex: 'primeiraEtiqueta',
                              key: 'primeiraEtiqueta',
                              align: 'center',
                            },
                            {
                              title: 'Última',
                              dataIndex: 'ultimaEtiqueta',
                              key: 'ultimaEtiqueta',
                              align: 'center',
                            }
                          ]}
                          className="productivity-table"
                        />
                      </div>
                    </div>
                  )
                }
              ]}
            />
          </div>
        </main>
      </div>

      {/* Footer */}
      <Footer onOpenModal={handleOpenModal} />

      <Modal
        title="Selecionar Impressora"
        open={printerModalVisible}
        onCancel={() => { setPrinterModalVisible(false); setReprintError(''); }}
        footer={null}
        width={600}
        centered
        maskClosable={false}
      >
        <PrinterSelection onPrinterConnect={() => { setPrinterModalVisible(false); setReprintError(''); }} />
      </Modal>

      <Modal
        title="Selecionar Veículo em Uso"
        open={vehicleSelectionVisible}
        onCancel={() => setVehicleSelectionVisible(false)}
        footer={null}
        width={720}
        centered
        destroyOnHidden
      >
        <Table
          rowKey={(row) => String(row.id ?? `${row.placa}-${row.dia}`)}
          dataSource={userUsages}
          loading={vehicleSelectionLoading}
          pagination={{ pageSize: 5 }}
          size="small"
          columns={[
            {
              title: 'Placa',
              dataIndex: 'placa',
              key: 'placa'
            },
            {
              title: 'Início',
              key: 'inicio',
              render: (_, row) => {
                const v = row.dtInicio ?? row.dtUso ?? row.dia;
                return v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-';
              }
            },
            {
              title: 'Fim',
              key: 'fim',
              render: (_, row) => {
                const v = row.dtFim ?? null;
                return v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-';
              }
            },
            {
              title: 'Status',
              key: 'status',
              render: (_, row) => {
                const aberto = !row.dtFim;
                const toSpYYYYMMDD = (date) => {
                  try {
                    const d = new Date(date);
                    const parts = new Intl.DateTimeFormat('sv-SE', {
                      timeZone: 'America/Sao_Paulo',
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    }).formatToParts(d);
                    const get = (t) => parts.find((p) => p.type === t)?.value || '00';
                    return `${get('year')}-${get('month')}-${get('day')}`;
                  } catch {
                    return '';
                  }
                };
                const hojeSp = toSpYYYYMMDD(new Date());
                const fimSp = row.dtFim ? toSpYYYYMMDD(row.dtFim) : '';
                const fechadoHoje = !!row.dtFim && !!fimSp && fimSp === hojeSp;
                if (aberto) return (<Tag color="green">Aberto</Tag>);
                if (fechadoHoje) return (<Tag color="gold">Fechado Hoje</Tag>);
                return (<Tag>Fechado</Tag>);
              }
            },
            {
              title: 'Ações',
              key: 'acoes',
              render: (_, row) => {
                const aberto = !row.dtFim;
                const toSpYYYYMMDD = (date) => {
                  try {
                    const d = new Date(date);
                    const parts = new Intl.DateTimeFormat('sv-SE', {
                      timeZone: 'America/Sao_Paulo',
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    }).formatToParts(d);
                    const get = (t) => parts.find((p) => p.type === t)?.value || '00';
                    return `${get('year')}-${get('month')}-${get('day')}`;
                  } catch {
                    return '';
                  }
                };
                const hojeSp = toSpYYYYMMDD(new Date());
                const fimSp = row.dtFim ? toSpYYYYMMDD(row.dtFim) : '';
                const fechadoHoje = !!row.dtFim && !!fimSp && fimSp === hojeSp;
                return (
                  <Space>
                    {aberto && (
                      <Button size="small" danger onClick={() => {
                        setUsageToClose(row);
                        setVehicleSelectionVisible(false);
                      }}>Encerrar</Button>
                    )}
                    {fechadoHoje && (
                      <Button size="small" onClick={async () => {
                        try {
                          if (!row?.id) return;
                          await apiService.veiculoUso.update(row.id, { dtFim: null });
                          const atualizado = await apiService.veiculoUso.get(row.id);
                          const novo = atualizado?.id ? atualizado : { ...row, dtFim: null };
                          setVeiculoDoDia(novo);
                          setUserUsages((prev) => prev.map((u) => (String(u.id) === String(row.id) ? novo : u)));
                          message.success('Coletas reabertas para hoje.');
                        } catch (e) {
                          message.error(e?.message || 'Falha ao reabrir coletas');
                        }
                      }}>Reabrir</Button>
                    )}
                  </Space>
                );
              }
            }
          ]}
        />
      </Modal>

      <InclusaoNotasModal
        visible={modalVisible}
        onClose={async () => {
          setModalVisible(false);
          setPendingBarcode('');
          try { setNotaAtual(null); } catch { }
          try { setNotaEncontradaModal(null); } catch { }
          try {
            const vehicleId = veiculoDoDia?.veiculo?.id || veiculoDoDia?.veiculoId;
            const diaLocal = onboardData?.dtUso || (() => { const hoje = new Date(); hoje.setHours(0, 0, 0, 0); return toLocalYYYYMMDD(hoje); })();
            const usuarioId = user?.id;
            if (vehicleId && diaLocal && usuarioId) {
              await buscarNotasTotais(vehicleId, diaLocal, usuarioId);
            }
          } catch { }
          try {
            const cnpj = clienteSelecionado?.CdInscricao || null;
            if (cnpj) {
              await fetchClientNotes(cnpj);
            }
          } catch { }
        }}
        prefillCode={pendingBarcode}
        notaEncontrada={notaEncontradaModal}
      />

      <VehiclePlateModal
        visible={modalPlacaVisible || !!usageToClose}
        mode={usageToClose ? 'close' : 'register'}
        usageToClose={usageToClose || null}
        onCancel={() => { setModalPlacaVisible(false); setUsageToClose(null); }}
        onSuccess={async (uso) => {
          setVeiculoDoDia(uso);
          updateOnboardData({ vehicleData: { ...(onboardData?.vehicleData || {}), placa: uso?.veiculo?.placa || uso?.placa || '' } });
          setModalPlacaVisible(false);
          setUsageToClose(null);
          setNotasCarregadas(false);
          try {
            const vehicleId = uso?.veiculo?.id || uso?.veiculoId;
            const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
            const diaLocal = toLocalYYYYMMDD(hoje);
            const usuarioId = user?.id;
            if (vehicleId && diaLocal && usuarioId) {
              await buscarNotasTotais(vehicleId, diaLocal, usuarioId);
            }
          } catch { }
        }}
        onCloseUsageSuccess={async () => {
          // Após encerrar uso antigo, refletir fechamento e abrir modal para novo vínculo
          try {
            const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
            const diaLocal = toLocalYYYYMMDD(hoje);
            const placaLookup = veiculoDoDia?.placa || veiculoDoDia?.veiculo?.placa || onboardData?.vehicleData?.placa;
            const usos = await apiService.veiculoUso.list({ usuarioCodigo: String(user?.codigo || ''), dia: diaLocal });
            const arr = Array.isArray(usos?.data) ? usos.data : Array.isArray(usos?.items) ? usos.items : Array.isArray(usos) ? usos : [];
            const novo = arr.find(u => (u?.placa ?? u?.veiculo?.placa) === placaLookup) || arr[0] || null;
            if (novo) setVeiculoDoDia(novo);
          } catch { }
          setUsageToClose(null);
          if (buscaPath !== 'ssw') {
            setModalPlacaVisible(true);
          }
          setNotasCarregadas(false);
        }}
      />

      {/* Removido: Modal de Criação de romaneio */}

      {/* Modal de Roteirização */}
      <Modal
        title="Roteirização"
        open={roteirizacaoModalVisible}
        onCancel={() => setRoteirizacaoModalVisible(false)}
        footer={null}
        width={800}
        style={{ top: 20 }}
        destroyOnHidden
      >
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p>Funcionalidade de roteirização em desenvolvimento.</p>
          <p>Esta funcionalidade permitirá otimizar as rotas de entrega.</p>
        </div>
      </Modal>

      {/* Modal de Centros de Distribuição */}
      <Modal
        title="Centros de Distribuição"
        open={cdModalVisible}
        onCancel={() => setCdModalVisible(false)}
        footer={null}
        width={1000}
        style={{ top: 20 }}
        destroyOnHidden
      >
        <CDComponent embedded />
      </Modal>

      {/* Modal de Encerrar Coletas */}
      <Modal
        title="Encerrar / Reabrir Coletas"
        open={encerrarModalVisible}
        onCancel={() => setEncerrarModalVisible(false)}
        footer={null}
        width={400}
        centered
        destroyOnHidden
      >
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p>Funcionalidade para encerrar ou reabrir coletas.</p>
          <p>Em desenvolvimento.</p>
        </div>
      </Modal>

    </>
  );
};

export default Onboard;
